import { type DatabasesPingRegistry } from '@databases/ping-registry';

import { type PresenceStateInterface } from './contracts/presence-state-interface';
import { type Participant } from './types';
import { type FieldEntryGrouping } from './types';
import {
  getGrouping,
  getKey,
  isCellGrouping,
  isColumnGrouping,
  isRowGrouping
} from './utils';

export class PresenceState implements PresenceStateInterface {
  /**
   * Participants will be stored in a map where the key is "<fieldId>-<entryId>". One of these ids can be
   * undefined representing an entire field/entry is selected. For example, if the key is just "-<entryId>", it
   * means the entire entry is selected.
   */
  #participants: Map<string, Participant[]> = new Map();

  /** Key is "<accountId>" and value is the "<fieldId>-<entryId>" hash. */
  #participantsByAccountId: Map<string, string> = new Map();

  constructor(private ping: DatabasesPingRegistry) {}

  addParticipant(grouping: FieldEntryGrouping, participant: Participant): void {
    this.removeParticipant(participant);

    const key = getKey(grouping);

    if (!this.#participants.has(key)) {
      this.#participants.set(key, []);
    }

    this.#participantsByAccountId.set(participant.identity.accountId, key);
    this.#participants.get(key)?.unshift(participant);
    this.#notifyRegisteredComponents(key);
  }

  getParticipantsFromGrouping(grouping: FieldEntryGrouping): Participant[] {
    const key = getKey(grouping);
    const participants = this.#participants.get(key);

    return participants ?? [];
  }

  get groupingToParticipantsMap() {
    return this.#participants;
  }

  get accountIdToParticipantsMap() {
    return this.#participantsByAccountId;
  }

  getRecentCollaboratorColor(grouping: FieldEntryGrouping): string | undefined {
    const participants = this.getParticipantsFromGrouping(grouping);

    return participants.length > 0 ? participants[0].colorToken : undefined;
  }

  removeParticipant(participant: Participant): void {
    const key = this.#participantsByAccountId.get(participant.identity.accountId);

    if (!key) {
      return;
    }

    // remove participant from old cell
    const participants = this.#participants.get(key);
    const newParticipants = participants?.filter(
      (p) => p.identity.accountId !== participant.identity.accountId
    );

    if (participants && newParticipants) {
      this.#participants.set(key, newParticipants);
    }

    // if no participants left, remove the key
    if (newParticipants?.length === 0) {
      this.#participants.delete(key);
    }

    this.#participantsByAccountId.delete(participant.identity.accountId);
    this.#notifyRegisteredComponents(key);
  }

  #notifyRegisteredComponents(k: string) {
    const grouping = getGrouping(k);
    const allParticipants = this.#participants.get(k) || [];
    let pingKey: ReturnType<typeof this.ping.key<'cell' | 'column' | 'row'>> | undefined;

    if (isCellGrouping(grouping)) {
      pingKey = this.ping.key('cell', grouping);
    } else if (isColumnGrouping(grouping)) {
      pingKey = this.ping.key('column', grouping);
    } else if (isRowGrouping(grouping)) {
      pingKey = this.ping.key('row', grouping);
    }

    if (pingKey) {
      this.ping.notify(pingKey, 'presence', allParticipants);
    }
  }
}
